import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
var _excluded = ["prefixCls", "bordered", "showCount", "maxLength", "className", "style", "size", "disabled", "onCompositionStart", "onCompositionEnd", "onChange", "onFocus", "onBlur", "status", "placeholder", "label", "errors", "warnings", "ErrorNode"];
import classNames from "classnames";
import RcTextArea from "rc-textarea";
import useMergedState from "rc-util/lib/hooks/useMergedState";
import omit from "rc-util/lib/omit";
import * as React from "react";
import { ConfigContext } from "../ConfigProvider";
import DisabledContext from "../ConfigProvider/DisabledContext";
import SizeContext from "../ConfigProvider/SizeContext";
import { FormItemInputContext } from "../Form/hack/context";
import { getMergedStatus, getStatusClassNames } from "../hack/_util/statusUtils";
import useState from "../hack/rc-util/hooks/useState";
import ClearableLabeledInput from "./ClearableLabeledInput";
import { fixControlledValue, resolveOnChange, triggerFocus } from "./hack";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function fixEmojiLength(value, maxLength) {
  return _toConsumableArray(value || "").slice(0, maxLength).join("");
}
function setTriggerValue(isCursorInEnd, preValue, triggerValue, maxLength) {
  var newTriggerValue = triggerValue;
  if (isCursorInEnd) {
    // 光标在尾部，直接截断
    newTriggerValue = fixEmojiLength(triggerValue, maxLength);
  } else if (_toConsumableArray(preValue || "").length < triggerValue.length && _toConsumableArray(triggerValue || "").length > maxLength) {
    // 光标在中间，如果最后的值超过最大值，则采用原先的值
    newTriggerValue = preValue;
  }
  return newTriggerValue;
}
var TextArea = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _classNames, _classNames2;
  var customizePrefixCls = _ref.prefixCls,
    _ref$bordered = _ref.bordered,
    bordered = _ref$bordered === void 0 ? true : _ref$bordered,
    _ref$showCount = _ref.showCount,
    showCount = _ref$showCount === void 0 ? false : _ref$showCount,
    maxLength = _ref.maxLength,
    className = _ref.className,
    style = _ref.style,
    customizeSize = _ref.size,
    customDisabled = _ref.disabled,
    onCompositionStart = _ref.onCompositionStart,
    onCompositionEnd = _ref.onCompositionEnd,
    onChange = _ref.onChange,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    customStatus = _ref.status,
    placeholder = _ref.placeholder,
    label = _ref.label,
    errors = _ref.errors,
    warnings = _ref.warnings,
    ErrorNode = _ref.ErrorNode,
    props = _objectWithoutProperties(_ref, _excluded);
  var _React$useContext = React.useContext(ConfigContext),
    getPrefixCls = _React$useContext.getPrefixCls,
    direction = _React$useContext.direction;
  var size = React.useContext(SizeContext);

  // ===================== Disabled =====================
  var disabled = React.useContext(DisabledContext);
  var mergedDisabled = customDisabled || disabled;
  var _React$useContext2 = React.useContext(FormItemInputContext),
    contextStatus = _React$useContext2.status,
    hasFeedback = _React$useContext2.hasFeedback,
    isFormItemInput = _React$useContext2.isFormItemInput,
    feedbackIcon = _React$useContext2.feedbackIcon;
  var mergedStatus = getMergedStatus(contextStatus, customStatus);
  var innerRef = React.useRef(null);
  var clearableInputRef = React.useRef(null);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    compositing = _React$useState2[0],
    setCompositing = _React$useState2[1];
  var oldCompositionValueRef = React.useRef();
  var oldSelectionStartRef = React.useRef(0);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var _useMergedState = useMergedState(props.defaultValue, {
      value: props.value
    }),
    _useMergedState2 = _slicedToArray(_useMergedState, 2),
    value = _useMergedState2[0],
    setValue = _useMergedState2[1];
  var hidden = props.hidden;
  var handleSetValue = function handleSetValue(val, callback) {
    if (props.value === undefined) {
      setValue(val);
      callback === null || callback === void 0 ? void 0 : callback();
    }
  };

  // =========================== Value Update ===========================
  // Max length value
  var hasMaxLength = Number(maxLength) > 0;
  var onInternalCompositionStart = function onInternalCompositionStart(e) {
    setCompositing(true);
    // 拼音输入前保存一份旧值
    oldCompositionValueRef.current = value;
    // 保存旧的光标位置
    oldSelectionStartRef.current = e.currentTarget.selectionStart;
    onCompositionStart === null || onCompositionStart === void 0 ? void 0 : onCompositionStart(e);
  };
  var onInternalCompositionEnd = function onInternalCompositionEnd(e) {
    setCompositing(false);
    var triggerValue = e.currentTarget.value;
    if (hasMaxLength) {
      var _oldCompositionValueR;
      var isCursorInEnd = oldSelectionStartRef.current >= maxLength + 1 || oldSelectionStartRef.current === ((_oldCompositionValueR = oldCompositionValueRef.current) === null || _oldCompositionValueR === void 0 ? void 0 : _oldCompositionValueR.length);
      triggerValue = setTriggerValue(isCursorInEnd, oldCompositionValueRef.current, triggerValue, maxLength);
    }
    // Patch composition onChange when value changed
    if (triggerValue !== value) {
      handleSetValue(triggerValue);
      resolveOnChange(e.currentTarget, e, onChange, triggerValue);
    }
    onCompositionEnd === null || onCompositionEnd === void 0 ? void 0 : onCompositionEnd(e);
  };
  var handleChange = function handleChange(e) {
    var triggerValue = e.target.value;
    if (!compositing && hasMaxLength) {
      // 1. 复制粘贴超过maxlength的情况 2.未超过maxlength的情况
      var isCursorInEnd = e.target.selectionStart >= maxLength + 1 || e.target.selectionStart === triggerValue.length || !e.target.selectionStart;
      triggerValue = setTriggerValue(isCursorInEnd, value, triggerValue, maxLength);
    }
    handleSetValue(triggerValue);
    resolveOnChange(e.currentTarget, e, onChange, triggerValue);
  };
  var handleFocus = React.useCallback(function (e) {
    setFocused(true);
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
  }, [onFocus]);
  var handleBlur = React.useCallback(function (e) {
    setFocused(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
  }, [onBlur]);

  // ============================== Reset ===============================
  var handleReset = function handleReset(e) {
    var _innerRef$current, _innerRef$current2, _innerRef$current2$re;
    handleSetValue("");
    (_innerRef$current = innerRef.current) === null || _innerRef$current === void 0 ? void 0 : _innerRef$current.focus();
    resolveOnChange((_innerRef$current2 = innerRef.current) === null || _innerRef$current2 === void 0 ? void 0 : (_innerRef$current2$re = _innerRef$current2.resizableTextArea) === null || _innerRef$current2$re === void 0 ? void 0 : _innerRef$current2$re.textArea, e, onChange);
  };
  var prefixCls = getPrefixCls("input", customizePrefixCls);
  React.useImperativeHandle(ref, function () {
    var _innerRef$current3;
    return {
      resizableTextArea: (_innerRef$current3 = innerRef.current) === null || _innerRef$current3 === void 0 ? void 0 : _innerRef$current3.resizableTextArea,
      focus: function focus(option) {
        var _innerRef$current4, _innerRef$current4$re;
        triggerFocus((_innerRef$current4 = innerRef.current) === null || _innerRef$current4 === void 0 ? void 0 : (_innerRef$current4$re = _innerRef$current4.resizableTextArea) === null || _innerRef$current4$re === void 0 ? void 0 : _innerRef$current4$re.textArea, option);
      },
      blur: function blur() {
        var _innerRef$current5;
        return (_innerRef$current5 = innerRef.current) === null || _innerRef$current5 === void 0 ? void 0 : _innerRef$current5.blur();
      }
    };
  });
  var textArea = /*#__PURE__*/_jsx(RcTextArea, _objectSpread(_objectSpread({}, omit(props, ["allowClear"])), {}, {
    disabled: mergedDisabled,
    className: classNames((_classNames = {}, _defineProperty(_classNames, "".concat(prefixCls, "-borderless"), !bordered), _defineProperty(_classNames, className, className && !showCount), _defineProperty(_classNames, "".concat(prefixCls, "-sm"), size === "small" || customizeSize === "small"), _defineProperty(_classNames, "".concat(prefixCls, "-lg"), size === "large" || customizeSize === "large"), _classNames), getStatusClassNames(prefixCls, mergedStatus)),
    style: showCount ? undefined : style,
    prefixCls: prefixCls,
    placeholder: label ? undefined : placeholder,
    onCompositionStart: onInternalCompositionStart,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onCompositionEnd: onInternalCompositionEnd,
    ref: innerRef
  }));
  var val = fixControlledValue(value);
  if (!compositing && hasMaxLength && (props.value === null || props.value === undefined)) {
    // fix #27612 将value转为数组进行截取，解决 '😂'.length === 2 等emoji表情导致的截取乱码的问题
    val = fixEmojiLength(val, maxLength);
  }
  placeholder = label ? label : placeholder;

  // TextArea
  var textareaNode = /*#__PURE__*/_jsx(ClearableLabeledInput, _objectSpread(_objectSpread({
    disabled: mergedDisabled
  }, props), {}, {
    prefixCls: prefixCls,
    direction: direction,
    inputType: "text",
    value: val,
    element: textArea,
    handleReset: handleReset,
    ref: clearableInputRef,
    bordered: bordered,
    status: customStatus,
    style: showCount ? undefined : style
  }));

  // Only show text area wrapper when needed
  // TODO: 暂不支持 showCount
  // if (showCount || hasFeedback) {
  //     const valueLength = [...val].length;

  //     let dataCount = "";
  //     if (typeof showCount === "object") {
  //         dataCount = showCount.formatter({ count: valueLength, maxLength });
  //     } else {
  //         dataCount = `${valueLength}${hasMaxLength ? ` / ${maxLength}` : ""}`;
  //     }

  //     return (
  //         <div
  //             hidden={hidden}
  //             className={classNames(
  //                 `${prefixCls}-textarea`,
  //                 {
  //                     [`${prefixCls}-textarea-rtl`]: direction === "rtl",
  //                     [`${prefixCls}-textarea-show-count`]: showCount,
  //                     [`${prefixCls}-textarea-in-form-item`]: isFormItemInput,
  //                 },
  //                 getStatusClassNames(`${prefixCls}-textarea`, mergedStatus, hasFeedback),
  //                 className,
  //             )}
  //             style={style}
  //             data-count={dataCount}
  //         >
  //             {textareaNode}
  //             {hasFeedback && <span className={`${prefixCls}-textarea-suffix`}>{feedbackIcon}</span>}
  //         </div>
  //     );
  // }

  return /*#__PURE__*/_jsxs("div", {
    className: classNames("".concat(prefixCls, "-wrap"), (_classNames2 = {}, _defineProperty(_classNames2, "".concat(prefixCls, "-wrap-status-error"), mergedStatus === "error"), _defineProperty(_classNames2, "".concat(prefixCls, "-wrap-disabled"), mergedDisabled), _defineProperty(_classNames2, "".concat(prefixCls, "-wrap-has-value"), value !== undefined && value !== "" && value !== null), _defineProperty(_classNames2, "".concat(prefixCls, "-wrap-focused"), focused), _defineProperty(_classNames2, "".concat(prefixCls, "-wrap-lg"), size === "large"), _defineProperty(_classNames2, "".concat(prefixCls, "-wrap-sm"), size === "small"), _classNames2)),
    children: [/*#__PURE__*/_jsxs("div", {
      className: classNames("".concat(prefixCls, "-inside-wrap"), {}),
      children: [textareaNode, !!label && /*#__PURE__*/_jsx("label", {
        className: "".concat(prefixCls, "-label"),
        children: placeholder
      })]
    }), /*#__PURE__*/_jsx("fieldset", {
      className: "".concat(prefixCls, "-outline"),
      children: /*#__PURE__*/_jsx("legend", {
        className: classNames("".concat(prefixCls, "-label-bg"), _defineProperty({}, "".concat(prefixCls, "-label-bg-none"), !label)),
        children: /*#__PURE__*/_jsx("span", {
          children: placeholder
        })
      })
    })]
  });
});
export default TextArea;